import Checkbox from '@atlaskit/checkbox';
import { Box, xcss } from '@atlaskit/primitives';
import { useDatabaseContent } from '@databases/database-state';
import FieldDecorator from '@databases/form/FieldDecorator';
import { messages } from '@databases/intl/messages';
import { useMentionNotification } from '@databases/mention-notification';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseFieldType, USER_VALUE_CURRENT } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { isNonEmptyUserValue } from '@databases/utils/database/field-types/user';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import { loadUserById } from '@databases/utils/redux/users/actions';
import { selectUserByIdOptional } from '@databases/utils/redux/users/selectors';
import { generateId } from '@databases/utils/utils';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { useIntl } from 'react-intl-next';

import { PopoverEdit } from '../PopoverEdit.tsx';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection.tsx';
import { type EditComponentProps } from '../types.ts';

import UserSelect from './UserSelect.tsx';
import { UserPopoverValue } from './UserValue.tsx';
import { type ResolvedUser, type Value } from './types.ts';

const UserPopoverContainerFlexWrapStyles = xcss({
  display: 'flex',
  flexWrap: 'wrap'
});

const UserValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.USER>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  entryId,
  isDefaultValue,
  isRenderedInSidePanel,
  isBulkEdit,
  ariaLabel,
  initialKey
}) => {
  const { contentId } = useDatabaseContent();
  const currentUserId = defaultStoreUtils.useSelector(selectCurrentUserId);
  const currentUser = defaultStoreUtils.useSelector((state) =>
    selectUserByIdOptional(state, { accountId: currentUserId })
  );
  const { formatMessage } = useIntl();
  const { scheduleMentionNotification, removeMentionNotification, flushMentions } =
    useMentionNotification({ contentId });
  const flushMentionsRef = useRef(flushMentions);
  flushMentionsRef.current = flushMentions;

  useEffect(() => {
    return () => {
      flushMentionsRef.current();
    };
  }, []);

  const defaultId = useMemo(() => generateId(), []);
  const addDataSectionRef = useRef<HTMLDivElement | null>(null);

  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useCallback(async () => {
      if (value === USER_VALUE_CURRENT && currentUserId) {
        await dispatch(loadUserById(currentUserId));
      }
    }, [currentUserId, dispatch, value])
  );

  usePopoverSoftSelection({
    entryId,
    fieldId: field.id,
    isRenderedInSidePanel
  });

  const resolvedValues: ResolvedUser[] = defaultStoreUtils.useEqualSelector(
    (state): Value[] => {
      let result: Value[];
      if (value === USER_VALUE_CURRENT) {
        result = currentUser ? [{ ...currentUser, _id: defaultId }] : [];
      } else {
        // Use updated display names if a user was renamed
        result = value.map((v) => {
          const user = isNonEmptyUserValue(v)
            ? selectUserByIdOptional(state, { accountId: v.accountId })
            : undefined;
          return user ? { ...v, displayName: user.displayName } : v;
        });
      }
      return result;
    }
  );

  const isMulti =
    (field.settings.multi || (!isBulkEdit && resolvedValues.length > 1)) &&
    !(isRenderedInSidePanel && value === USER_VALUE_CURRENT);

  const handleToggleCurrent = useCallback(() => {
    if (value === USER_VALUE_CURRENT) {
      const userValues = currentUser ? [{ ...currentUser, _id: defaultId }] : [];
      onChange(userValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.SET_TO,
          value: userValues
        }
      });
    } else {
      onChange(USER_VALUE_CURRENT, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.SET_TO,
          value: USER_VALUE_CURRENT
        }
      });
    }
  }, [defaultId, currentUser, onChange, value]);

  const UserSelectAddDataSection = useMemo(
    () => (
      <div ref={addDataSectionRef}>
        <UserSelect
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          contentId={contentId}
          field={field}
          resolvedUsers={resolvedValues}
          isRenderedInSidePanel={isRenderedInSidePanel}
          isMulti={isMulti}
          addDataSectionRef={addDataSectionRef}
          aria-label={ariaLabel}
          scheduleMentionNotification={scheduleMentionNotification}
          initialKey={initialKey}
        />
      </div>
    ),
    [
      onChange,
      onFocus,
      onBlur,
      autoFocus,
      contentId,
      field,
      resolvedValues,
      isRenderedInSidePanel,
      isMulti,
      ariaLabel,
      scheduleMentionNotification,
      initialKey
    ]
  );

  const CurrentDataSection = useMemo(
    () => (
      <Box xcss={UserPopoverContainerFlexWrapStyles}>
        {resolvedValues.map((user) => {
          return (
            <UserPopoverValue
              key={user._id}
              user={user}
              onChange={onChange}
              resolvedUsers={resolvedValues}
              addDataSectionRef={addDataSectionRef}
              removeMentionNotification={removeMentionNotification}
              fieldId={field.id}
            />
          );
        })}
      </Box>
    ),
    [resolvedValues, onChange, removeMentionNotification, field.id]
  );

  const Popover = useMemo(
    () => (
      <PopoverEdit
        isMultiSelect={isMulti}
        hasCurrentData={value.length > 0}
        currentDataSection={CurrentDataSection}
        addDataSection={UserSelectAddDataSection}
        isRenderedInSidePanel={isRenderedInSidePanel}
        fieldWidth={field.width}
        isBulkEdit={isBulkEdit}
      />
    ),
    [
      isMulti,
      value.length,
      CurrentDataSection,
      UserSelectAddDataSection,
      isRenderedInSidePanel,
      field,
      isBulkEdit
    ]
  );

  if (isDefaultValue) {
    return (
      <>
        {Popover}
        <FieldDecorator isCompact>
          <Checkbox
            isChecked={value === USER_VALUE_CURRENT}
            onChange={handleToggleCurrent}
            onFocus={onFocus}
            onBlur={onBlur}
            label={formatMessage(
              messages['field-settings.type-user-set-current-user-default-text']
            )}
          />
        </FieldDecorator>
      </>
    );
  } else {
    return Popover;
  }
};

export default UserValuePopoverEdit;
